// src/data/leasesPortfolioClient.js
//
// Slice U5b — read-only client for the two portfolio lease intelligence
// endpoints landed in U5a:
//
//   GET /api/critical-dates
//   GET /api/watch-items
//
// Both are server-aggregated joins (CD/WI → leases → locations), already
// cached for 60s server-side. We add a 5-minute client-side cache via
// services/leasesPortfolioCache.js, full-busted by every portfolio write
// through services/cacheBus.js (same pattern as reportsClient).
//
// Per CONTRACT.md ("Portfolio lease intelligence"):
//   - viewer-readable
//   - no audit emission (reads)
//   - server returns camelCase rows already
//   - categoryRaw and areas preserve bounded notice-period metadata
//   - portfolio lease rows include earliestTermination and termType
//   - curated identity: address/suite from office, vendor/landlord (not contact),
//     lessee, leaseIdSpacebase, and existing structured areas[].name; nullable.
//   - errors are typed instances from src/data/errors.js

import {
  AuthError,
  NetworkError,
  errorFromResponse,
} from "./errors.js";
import {
  KEY_CRITICAL_DATES,
  KEY_WATCH_ITEMS,
  KEY_UNSPENT_ALLOWANCES,
  KEY_UNSPENT_ALLOWANCE_ROWS,
  KEY_PORTFOLIO_LEASES,
  get as cacheGet,
  set as cacheSet,
  getGeneration as cacheGeneration,
} from "../services/leasesPortfolioCache.js";
import { log } from "../utils/logger.js";

// ─── Internal: same fetch wrapper shape as reportsClient ──────────────────

async function jsonRequest(url) {
  let res;
  try {
    res = await fetch(url, {
      method: "GET",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
    });
  } catch (err) {
    log.error("leasesPortfolioClient: network failure", { url, err: String(err) });
    throw new NetworkError(`Failed to reach ${url}`, err);
  }
  if (!res.ok) {
    const e = await errorFromResponse(res);
    log.warn("leasesPortfolioClient: non-2xx", { url, status: res.status, name: e.name });
    throw e;
  }
  try {
    return await res.json();
  } catch (err) {
    throw new NetworkError(`Malformed JSON from ${url}`, err);
  }
}

// ─── Critical Dates ───────────────────────────────────────────────────────

/**
 * @typedef  {Object} CriticalDateRow
 * @property {string}      id
 * @property {string}      type             enum (renewal_notice, break_option, …)
 * @property {string|null} dueDate          YYYY-MM-DD
 * @property {string|null} description
 * @property {string|null} categoryRaw      Imported label used to identify bounded notice periods
 * @property {string|null} areas             Area scope used when pairing notice-period boundaries
 * @property {Object|null}  fieldSources     Per-date provenance; document review is required for decision use
 * @property {number|null} daysUntil        signed; negative = overdue
 * @property {string}      urgency          overdue | urgent | soon | watch | ok
 * @property {string}      leaseId
 * @property {string}      leaseStatus
 * @property {string}      locationId
 * @property {string}      locationMarket
 * @property {string|null} locationAlias
 * @property {string}      region           AMER | EMEA | APAC
 */

/**
 * @returns {Promise<CriticalDateRow[]>}
 * @throws  {NetworkError | AuthError}
 */
export async function getCriticalDates() {
  const cached = cacheGet(KEY_CRITICAL_DATES);
  if (cached) {
    log.api("leasesPortfolioClient.getCriticalDates cache hit", { count: cached.length });
    return cached;
  }
  const rows = await jsonRequest("/api/critical-dates");
  cacheSet(KEY_CRITICAL_DATES, rows);
  log.api("leasesPortfolioClient.getCriticalDates fetched", { count: rows.length });
  return rows;
}

// ─── Watch Items ──────────────────────────────────────────────────────────

/**
 * @typedef  {Object} WatchItemRow
 * @property {string}      id
 * @property {string}      type             risk | action | milestone | negotiation_intel
 * @property {string}      severity         high | medium | low
 * @property {string}      status           open | resolved | carry_to_renewal
 * @property {string}      title
 * @property {string|null} body
 * @property {string|null} resolvedAt
 * @property {string}      createdAt
 * @property {string|null} createdBy        display name
 * @property {string}      leaseId
 * @property {string}      leaseStatus
 * @property {string}      locationId
 * @property {string}      locationMarket
 * @property {string|null} locationAlias
 * @property {string}      region
 */

/**
 * @returns {Promise<WatchItemRow[]>}
 * @throws  {NetworkError | AuthError}
 */
export async function getWatchItems() {
  const cached = cacheGet(KEY_WATCH_ITEMS);
  if (cached) {
    log.api("leasesPortfolioClient.getWatchItems cache hit", { count: cached.length });
    return cached;
  }
  const rows = await jsonRequest("/api/watch-items");
  cacheSet(KEY_WATCH_ITEMS, rows);
  log.api("leasesPortfolioClient.getWatchItems fetched", { count: rows.length });
  return rows;
}

// ─── Unspent Allowances (slice M1.5a) ────────────────────────────────────
//
// Portfolio-wide unspent allowance dollars, broken down by currency. No
// FX rollup — mirrors the M1.1 mixed-currency directional KPI precedent
// for Annual Portfolio Cost. Empty array when no allowances exist.

/**
 * @typedef  {Object} UnspentAllowanceRow
 * @property {string} currency  ISO 4217 (USD, GBP, EUR, SGD, …)
 * @property {number} unspent   Currency-native dollars; >= 0
 */

/**
 * @typedef  {Object} UnspentAllowanceResponse
 * @property {UnspentAllowanceRow[]} unspentAllowanceByCurrency
 */

/**
 * @returns {Promise<UnspentAllowanceResponse>}
 * @throws  {NetworkError | AuthError}
 */
export async function getUnspentAllowances() {
  const cached = cacheGet(KEY_UNSPENT_ALLOWANCES);
  if (cached) {
    log.api("leasesPortfolioClient.getUnspentAllowances cache hit", {
      currencies: cached.unspentAllowanceByCurrency.length,
    });
    return cached;
  }
  const body = await jsonRequest("/api/unspent-allowances");
  cacheSet(KEY_UNSPENT_ALLOWANCES, body);
  log.api("leasesPortfolioClient.getUnspentAllowances fetched", {
    currencies: body.unspentAllowanceByCurrency.length,
  });
  return body;
}

// ─── Unspent Allowance Rows (near-expiry report) ─────────────────────────
//
// Individual unspent-allowance rows for the portfolio near-expiry table.
// The near-expiry threshold (≤90 days) is computed client-side from
// daysUntilExpiry so it can change without a server deploy.

/**
 * @typedef  {Object} UnspentAllowanceDetailRow
 * @property {string}      id
 * @property {string}      type             ti_allowance | moving_allowance | abatement | test_fit | other
 * @property {string|null} label
 * @property {string}      currency         ISO 4217
 * @property {number}      totalAmount      currency-native; >= 0
 * @property {number}      drawnToDate      currency-native; >= 0
 * @property {string}      expiryDate       YYYY-MM-DD
 * @property {number}      daysUntilExpiry  signed; 0 = expires today
 * @property {number}      drawsCount       number of draw records
 * @property {string}      leaseId
 * @property {string}      locationId
 * @property {string}      locationMarket
 * @property {string|null} locationAlias
 * @property {string}      region
 */

/**
 * @returns {Promise<UnspentAllowanceDetailRow[]>}
 * @throws  {NetworkError | AuthError}
 */
export async function getUnspentAllowanceRows() {
  const cached = cacheGet(KEY_UNSPENT_ALLOWANCE_ROWS);
  if (cached) {
    log.api("leasesPortfolioClient.getUnspentAllowanceRows cache hit", { count: cached.length });
    return cached;
  }
  const rows = await jsonRequest("/api/unspent-allowances/rows");
  cacheSet(KEY_UNSPENT_ALLOWANCE_ROWS, rows);
  log.api("leasesPortfolioClient.getUnspentAllowanceRows fetched", { count: rows.length });
  return rows;
}

// ─── Portfolio Leases (LSE-LANDING-PAGE) ─────────────────────────────────
//
// All non-terminated leases joined with location city + region, for the
// Leases landing page Gantt, KPI strip, and analytics panels.

/**
 * @typedef  {Object} PortfolioLeaseRow
 * @property {string|null} address          Office street address
 * @property {string|null} suite            Office suite identity
 * @property {string|null} vendor           Canonical lessor, not lessor_contact
 * @property {string|null} landlord         Same canonical vendor for baseline search
 * @property {string|null} lessee
 * @property {string|null} leaseIdSpacebase
 * @property {Object[]} areas               Existing structured names and area values
 * @property {string}      id
 * @property {string}      locationId
 * @property {string}      leaseType       direct_lease | serviced_office | sublease_inbound | sublease_outbound
 * @property {string}      status          active | expired | holdover | in_negotiation | sublease_out
 * @property {string|null} startDate       YYYY-MM-DD
 * @property {string|null} endDate         YYYY-MM-DD or 9999-12-31 sentinel
 * @property {string|null} breakDate       YYYY-MM-DD
 * @property {string|null} earliestTermination YYYY-MM-DD
 * @property {string|null} termType         Fixed | Recurring | Rolling | Month-to-Month | Year-to-Year | Other
 * @property {string|null} soonestPossibleExpiration  YYYY-MM-DD
 * @property {string|null} nextNoticeDeadline         YYYY-MM-DD
 * @property {Object|null}  fieldSources              Per-field provenance for verified decision dates
 * @property {number|null} totalCost
 * @property {number|null} rentPerSqft
 * @property {number|null} size
 * @property {string|null} areaType        SqFt | SqM | Seats
 * @property {string}      currency        ISO 4217
 * @property {string}      city            location market name
 * @property {string}      region          AMER | EMEA | APAC
 */

/**
 * @returns {Promise<PortfolioLeaseRow[]>}
 * @throws  {NetworkError | AuthError}
 */
export async function getPortfolioLeases() {
  const cached = cacheGet(KEY_PORTFOLIO_LEASES);
  if (cached) {
    log.api("leasesPortfolioClient.getPortfolioLeases cache hit", { count: cached.length });
    return cached;
  }
  const generation = cacheGeneration();
  const rows = await jsonRequest("/api/leases/portfolio");
  if (generation === cacheGeneration()) cacheSet(KEY_PORTFOLIO_LEASES, rows);
  log.api("leasesPortfolioClient.getPortfolioLeases fetched", { count: rows.length });
  return rows;
}

/**
 * LEASES-EDIT-INSTANT-UPDATE (location-detail path)
 * Merge `updatedLease` into the cached portfolio leases array so the next
 * cache-hit (e.g. when LeasesPage remounts after navigating back from
 * LocationDetail) returns the already-updated row without a server round-trip.
 * No-ops gracefully when the cache is empty or the lease is not found.
 *
 * @param {{ id: string } & Record<string, unknown>} updatedLease
 */
export function patchPortfolioLeasesCache(updatedLease) {
  if (!updatedLease?.id) return;
  const cached = cacheGet(KEY_PORTFOLIO_LEASES);
  if (!cached) return;
  const patched = cached.map((l) =>
    l.id === updatedLease.id ? { ...l, ...updatedLease } : l
  );
  cacheSet(KEY_PORTFOLIO_LEASES, patched);
  log.api("leasesPortfolioClient.patchPortfolioLeasesCache patched", { id: updatedLease.id });
}

// Re-export typed errors so callers only need to import from one place.
export { AuthError, NetworkError } from "./errors.js";
